/**
 * Overtime Create Form Modal Komponenti
 * 
 * Yöneticinin bir çalışana fazla mesai tanımlaması için kullanılan modal komponenti.
 * Form içerir: Çalışan seçimi, tarih, başlangıç-bitiş saati, açıklama, otomatik onay seçeneği.
 * 
 * Özellikler:
 * - Çalışan seçimi (dropdown)
 * - Tarih seçici
 * - Saat seçicileri (başlangıç-bitiş)
 * - Açıklama alanı
 * - Otomatik onay checkbox'ı
 * - Form validasyonu
 * - Süre hesaplama ve gösterimi
 */

import React, { useState, useEffect } from 'react';
import { Modal, Form, Select, DatePicker, TimePicker, Input, Checkbox, Alert } from 'antd';
import { useTranslation } from 'react-i18next';
import { OvertimeCreateDTO } from '../../../../types/overtime.types';
import { UserStatus } from '../../../../types/user.types';
import { RolesEnum } from '../../../../types/route.types';
import { useUsers } from '../../../../hooks/useUsers';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/tr';
import locale from 'antd/lib/date-picker/locale/tr_TR';

const { TextArea } = Input;
const { Option } = Select;

interface OvertimeCreateFormModalProps {
  visible: boolean;
  onSubmit: (data: OvertimeCreateDTO) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
  departmentId?: number; // Belirli bir departman için filtreleme (opsiyonel)
}

/**
 * Fazla mesai tanımlama formu modal komponenti
 * @param visible - Modal görünür mü?
 * @param onSubmit - Form submit callback'i
 * @param onCancel - İptal callback'i
 * @param loading - Yükleniyor durumu
 * @param departmentId - Departman filtresi (opsiyonel)
 */
const OvertimeCreateFormModal: React.FC<OvertimeCreateFormModalProps> = ({
  visible,
  onSubmit,
  onCancel,
  loading = false,
  departmentId,
}) => {
  const { t, i18n } = useTranslation();
  const [form] = Form.useForm();
  const { users, loading: usersLoading } = useUsers();
  
  // State'ler
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
  const [startTime, setStartTime] = useState<Dayjs | null>(null);
  const [endTime, setEndTime] = useState<Dayjs | null>(null);
  const [duration, setDuration] = useState<number>(0); // Dakika cinsinden
  const [autoApprove, setAutoApprove] = useState<boolean>(true);

  // Modal kapandığında formu temizle
  useEffect(() => {
    if (!visible) {
      form.resetFields();
      setSelectedDate(null);
      setStartTime(null);
      setEndTime(null);
      setDuration(0);
      setAutoApprove(true);
    }
  }, [visible, form]);

  // Süreyi hesapla (başlangıç ve bitiş saati değiştiğinde)
  useEffect(() => {
    if (startTime && endTime) {
      const diff = endTime.diff(startTime, 'minute');
      setDuration(diff > 0 ? diff : 0);
    } else {
      setDuration(0);
    }
  }, [startTime, endTime]);

  /**
   * Süreyi formatlar
   */
  const formatDuration = (minutes: number) => {
    if (minutes === 0) return '-';
    
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    
    if (hours === 0) {
      return `${mins} ${t('overtime.minute', 'dakika')}`;
    }
    
    return mins > 0 
      ? `${hours} ${t('overtime.hour', 'saat')} ${mins} ${t('overtime.minute', 'dakika')}` 
      : `${hours} ${t('overtime.hour', 'saat')}`;
  };

  /**
   * Form submit handler
   */
  const handleSubmit = async () => {
    try {
      // Form validasyonu yap
      const values = await form.validateFields();
      
      // DTO oluştur
      const overtimeData: OvertimeCreateDTO = {
        employeeId: values.employeeId,
        date: selectedDate!.format('YYYY-MM-DD'),
        startTime: startTime!.format('HH:mm'),
        endTime: endTime!.format('HH:mm'),
        description: values.description?.trim() || undefined,
        autoApprove: autoApprove,
      };
      
      // Callback'i çağır
      await onSubmit(overtimeData);
    } catch (error) {
      // Form validasyon hatası
      console.error('Form validation failed:', error);
    }
  };

  // Çalışanları filtrele (aktif, admin değil, isteğe bağlı departman)
  const filteredUsers = users.filter(user => {
    if (user.status !== UserStatus.ACTIVE) return false;
    if (user.role === RolesEnum.ADMIN || user.role === RolesEnum.SUPERADMIN) return false;
    if (departmentId && user.primaryDepartment?.id !== departmentId) return false;
    return true;
  });

  return (
    <Modal
      title={
        <div className="d-flex align-items-center gap-2">
          <i className="ti ti-plus text-primary" style={{ fontSize: '1.5rem' }} />
          <span>{t('overtime.createForEmployee', 'Çalışana Fazla Mesai Tanımla')}</span>
        </div>
      }
      open={visible}
      onOk={handleSubmit}
      onCancel={onCancel}
      confirmLoading={loading}
      okText={
        <span>
          <i className="ti ti-device-floppy me-1" />
          {t('overtime.save', 'Kaydet')}
        </span>
      }
      cancelText={t('overtime.cancel', 'İptal')}
      width={700}
      centered
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          autoApprove: true,
        }}
      >
        {/* Çalışan Seçimi */}
        <Form.Item
          label={
            <span className="fw-medium">
              {t('overtime.selectEmployee', 'Çalışan Seçin')}
              {' '}
              <span className="text-danger">*</span>
            </span>
          }
          name="employeeId"
          rules={[
            {
              required: true,
              message: t('overtime.employeeRequired', 'Çalışan seçimi zorunludur'),
            },
          ]}
        >
          <Select
            showSearch
            placeholder={t('overtime.selectEmployeePlaceholder', 'Bir çalışan seçin...')}
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.children as unknown as string)
                .toLowerCase()
                .includes(input.toLowerCase())
            }
            loading={usersLoading}
            disabled={loading}
            size="large"
          >
            {filteredUsers.map(user => (
              <Option key={user.id} value={user.id}>
                {user.firstName} {user.lastName}
                {user.primaryDepartment && (
                  <span className="text-muted"> - {user.primaryDepartment.name}</span>
                )}
              </Option>
            ))}
          </Select>
        </Form.Item>

        {/* Tarih Seçimi */}
        <Form.Item
          label={
            <span className="fw-medium">
              {t('overtime.date', 'Tarih')}
              {' '}
              <span className="text-danger">*</span>
            </span>
          }
          name="date"
          rules={[
            {
              required: true,
              message: t('overtime.dateRequired', 'Tarih seçimi zorunludur'),
            },
          ]}
        >
          <DatePicker
            style={{ width: '100%' }}
            format="DD.MM.YYYY"
            placeholder={t('overtime.selectDate', 'Tarih seçin')}
            locale={i18n.language === 'tr' ? locale : undefined}
            onChange={(date) => setSelectedDate(date)}
            disabled={loading}
            size="large"
            disabledDate={(current) => {
              // Geçmiş tarihler seçilebilir, çok ileri tarihler seçilemesin (3 ay sonrası)
              return current && current > dayjs().add(3, 'month');
            }}
          />
        </Form.Item>

        {/* Başlangıç ve Bitiş Saati */}
        <div className="row">
          <div className="col-md-6">
            <Form.Item
              label={
                <span className="fw-medium">
                  {t('overtime.startTime', 'Başlangıç Saati')}
                  {' '}
                  <span className="text-danger">*</span>
                </span>
              }
              name="startTime"
              rules={[
                {
                  required: true,
                  message: t('overtime.startTimeRequired', 'Başlangıç saati zorunludur'),
                },
              ]}
            >
              <TimePicker
                style={{ width: '100%' }}
                format="HH:mm"
                placeholder={t('overtime.selectTime', 'Saat seçin')}
                onChange={(time) => setStartTime(time)}
                disabled={loading}
                size="large"
                minuteStep={15}
              />
            </Form.Item>
          </div>
          <div className="col-md-6">
            <Form.Item
              label={
                <span className="fw-medium">
                  {t('overtime.endTime', 'Bitiş Saati')}
                  {' '}
                  <span className="text-danger">*</span>
                </span>
              }
              name="endTime"
              rules={[
                {
                  required: true,
                  message: t('overtime.endTimeRequired', 'Bitiş saati zorunludur'),
                },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const start = getFieldValue('startTime');
                    if (!value || !start || value.isAfter(start)) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      new Error(t('overtime.endTimeAfterStart', 'Bitiş saati başlangıçtan sonra olmalı'))
                    );
                  },
                }),
              ]}
            >
              <TimePicker
                style={{ width: '100%' }}
                format="HH:mm"
                placeholder={t('overtime.selectTime', 'Saat seçin')}
                onChange={(time) => setEndTime(time)}
                disabled={loading}
                size="large"
                minuteStep={15}
              />
            </Form.Item>
          </div>
        </div>

        {/* Süre Gösterimi */}
        {duration > 0 && (
          <Alert
            message={
              <span className="fw-medium">
                {t('overtime.calculatedDuration', 'Hesaplanan Süre')}: {formatDuration(duration)}
              </span>
            }
            type="info"
            showIcon
            icon={<i className="ti ti-clock" />}
            className="mb-3"
          />
        )}

        {/* Açıklama */}
        <Form.Item
          label={<span className="fw-medium">{t('overtime.description', 'Açıklama')}</span>}
          name="description"
          rules={[
            {
              max: 500,
              message: t('overtime.descriptionMaxLength', 'Açıklama maksimum 500 karakter olabilir'),
            },
          ]}
        >
          <TextArea
            rows={3}
            placeholder={t('overtime.descriptionPlaceholder', 'Fazla mesai nedeni...')}
            maxLength={500}
            showCount
            disabled={loading}
          />
        </Form.Item>

        {/* Otomatik Onay Checkbox */}
        <Form.Item name="autoApprove" valuePropName="checked">
          <Checkbox
            checked={autoApprove}
            onChange={(e) => setAutoApprove(e.target.checked)}
            disabled={loading}
          >
            <span className="fw-medium">
              {t('overtime.autoApprove', 'Otomatik onaylansın (direkt puantaja dahil)')}
            </span>
          </Checkbox>
        </Form.Item>

        {/* Bilgilendirme */}
        <Alert
          message={
            autoApprove
              ? t('overtime.autoApproveInfo', 'Bu seçenek işaretliyse, fazla mesai direkt onaylı olarak oluşturulur ve puantaja dahil edilir.')
              : t('overtime.manualApproveInfo', 'Fazla mesai beklemede durumunda oluşturulur. Daha sonra onaylamanız gerekir.')
          }
          type={autoApprove ? 'success' : 'warning'}
          showIcon
          icon={<i className={`ti ${autoApprove ? 'ti-check' : 'ti-alert-triangle'}`} />}
        />
      </Form>
    </Modal>
  );
};

export default OvertimeCreateFormModal;


